import { differenceInCalendarDays, format, isValid, parse } from 'date-fns';
import type { Locale } from 'date-fns';

/**
 * Statuses whose date is a settled event, so the Status column shows "Since <date>" (spec 5.3).
 * Two beyond the three the spec names. Blocked, because earthnode derives its date from the
 * "Account Blocked" note purely for this column and a blocked account isn't in a stage. Suspended,
 * because the admin Suspend action writes it to VIOAccounts.AccountStatus through the workflow's
 * SUSPEND_STATUS and stamps an onboarding-history row with it, so the date resolves like any other
 * — and AccountStatusSummaryCard already counts it as terminal.
 */
const DATED_STATUSES = new Set(['Active', 'Rejected', 'Disabled', 'Blocked', 'Suspended']);

export type StatusDateDisplay = { kind: 'since'; date: Date } | { kind: 'daysInStage'; numDays: number } | null;

/**
 * Every account date the portal renders goes through here — the list's Date Added column, the status
 * sub-label under a badge, the account card's activation date — so the format is stated once.
 */
export function formatAccountDate(date: Date, locale: Locale): string {
  // `PP` is the locale's own long date, so each language gets its own word order: "Jan 15, 2026" in
  // English, which is byte-identical to the fixed `MMM d, yyyy` this replaced, and "15 janv. 2026"
  // in fr-CA rather than the English order with French month names. The caller resolves the locale
  // through `useDateLocale`, so no language mapping lives here.
  return format(date, 'PP', { locale });
}

/** The same, for a raw earthnode value; an unparseable or absent date renders as nothing. */
export function formatApiDate(value: string | null | undefined, locale: Locale): string {
  const date = parseApiDateTime(value);

  return date ? formatAccountDate(date, locale) : '';
}

/** earthnode date-times arrive as "YYYY-MM-DD HH:mm:ss" (date-only for a few legacy fields). */
export function parseApiDateTime(value: string | null | undefined): Date | null {
  if (!value) return null;

  const dateTime = parse(value, 'yyyy-MM-dd HH:mm:ss', new Date());
  if (isValid(dateTime)) return dateTime;

  const dateOnly = parse(value, 'yyyy-MM-dd', new Date());

  return isValid(dateOnly) ? dateOnly : null;
}

/**
 * What the Status column shows under the badge: the date the account entered a settled status, or
 * how many days it has sat in its current onboarding stage. Nothing when the backend has no
 * recorded transition (`AccountStatusDate` null) or no status at all.
 */
export function getStatusDateDisplay(
  status: string,
  statusDate: string | null | undefined,
  now: Date = new Date(),
): StatusDateDisplay {
  const date = parseApiDateTime(statusDate);
  if (!status || !date) return null;

  if (DATED_STATUSES.has(status)) return { kind: 'since', date };

  return { kind: 'daysInStage', numDays: Math.max(0, differenceInCalendarDays(now, date)) };
}
